import { CustomizerEngine } from 'smg/customizer';
import { DEFAULT_FONT_FAMILY, FontConfig, FONTS, FontByName, RegisterDropdownFaces, ResolvedFontFace, ResolveFontFace, ResolveFontFamily } from '../product-customizer/fonts';
import { RibbonTextLayer } from './ribbon-text-layer';
import { DateTime } from 'luxon';
import { Debounce } from 'smg/util';

// Default text shown
const DEFAULT_TEXT_ONE = 'Your Text Here';
const DEFAULT_TEXT_TWO = 'Your Text Here';
const DEFAULT_TEXT_THREE = DateTime.now().year.toString();

// Baseline text for height measurement (uppercase with no descenders)
const BASELINE_TEXT = 'HELLO WORLD';

// Maximum perpendicular distance from path (in pixels) for each text layer
// Text will auto-shrink if it extends beyond this distance from the path centerline
const MAX_PATH_DISTANCE = {
    textOne: 45,          // Line 1 when Line 2 has content (text-path-1)
    textOneAlt: 50,       // Line 1 when Line 2 is empty (text-path-4) - wider band
    textTwo: 30,          // Line 2 (text-path-2)
    textThree: 40,        // Line 3 (text-path-3)
};

export class TextCustomizerUI {
    #engine: CustomizerEngine;

    // DOM elements
    #textOneInput: HTMLInputElement | null = null;
    #textTwoInput: HTMLInputElement | null = null;
    #textThreeInput: HTMLInputElement | null = null;
    #fontFamilyDropdown: HTMLElement | null = null;
    #fontFamilyBtn: HTMLButtonElement | null = null;
    #boldBtn: HTMLButtonElement | null = null;
    #italicBtn: HTMLButtonElement | null = null;
    #underlineBtn: HTMLButtonElement | null = null;

    // Per-layer font sizes (in pt)
    #fontSizes = { textOne: 7, textTwo: 6, textThree: 8 };
    static readonly #minFontSize = 5;
    static readonly #maxFontSize = 12;

    // Debounced shrink-to-fit functions for each layer
    #debouncedShrinkTextOne: () => void;
    #debouncedShrinkTextTwo: () => void;
    #debouncedShrinkTextThree: () => void;
    #debouncedCheckTextTwoIntersection: () => void;

    // Per-line color pickers
    #textOneColorPicker: HTMLInputElement | null = null;
    #textOneColorIndicator: HTMLElement | null = null;
    #textTwoColorPicker: HTMLInputElement | null = null;
    #textTwoColorIndicator: HTMLElement | null = null;
    #textThreeColorPicker: HTMLInputElement | null = null;
    #textThreeColorIndicator: HTMLElement | null = null;

    // Font style state
    #fontStyles = { bold: false, italic: false, underline: false };

    // Currently selected menu font
    #currentFont: FontConfig = FONTS[0];

    // Invalidates in-flight face fetches when a newer family/style change starts
    #faceSeq = 0;

    /** Resolves once the default font face is loaded and applied to the layers. */
    readonly ready: Promise<void>;

    // Offscreen canvas for text height measurement
    #measureCanvas: HTMLCanvasElement;
    #measureCtx: CanvasRenderingContext2D;

    // Track whether line 1 is using the alternate path (when line 2 is empty)
    #textOneUsingAltPath = false;

    constructor(engine: CustomizerEngine) {
        // Create offscreen canvas for text measurement
        this.#measureCanvas = document.createElement('canvas');
        this.#measureCanvas.width = 500;
        this.#measureCanvas.height = 200;
        const ctx = this.#measureCanvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not get 2d context for measurement canvas');
        }
        this.#measureCtx = ctx;
        this.#engine = engine;

        // Initialize debounced shrink-to-fit functions (150ms delay)
        this.#debouncedShrinkTextOne = Debounce(() => {
            void this.#shrinkToFitLayer('text-one', 'textOne', this.#getTextOneMaxDistance());
        }, 150, false);
        this.#debouncedShrinkTextTwo = Debounce(() => {
            void this.#shrinkTextTwoWithIntersectionCheck();
        }, 150, false);
        this.#debouncedCheckTextTwoIntersection = Debounce(() => {
            void this.#shrinkTextTwoToAvoidIntersection();
        }, 150, false);
        this.#debouncedShrinkTextThree = Debounce(() => {
            void this.#shrinkToFitLayer('text-three', 'textThree', MAX_PATH_DISTANCE.textThree);
        }, 150, false);

        this.#initializeUI();
        this.ready = this.#applyFontFamily(DEFAULT_FONT_FAMILY);
    }

    /** Gets all three text layers, returns [textOne, textTwo, textThree] */
    #getTextLayers(): [RibbonTextLayer | undefined, RibbonTextLayer | undefined, RibbonTextLayer | undefined] {
        const textOne = this.#engine.getLayer('text-one') as RibbonTextLayer | undefined;
        const textTwo = this.#engine.getLayer('text-two') as RibbonTextLayer | undefined;
        const textThree = this.#engine.getLayer('text-three') as RibbonTextLayer | undefined;
        return [textOne, textTwo, textThree];
    }

    /** Applies a function to both text layers */
    #applyToAllLayers(fn: (layer: RibbonTextLayer) => void): void {
        const [textOne, textTwo, textThree] = this.#getTextLayers();
        if (textOne) {
            fn(textOne);
        }
        if (textTwo) {
            fn(textTwo);
        }
        if (textThree) {
            fn(textThree);
        }
    }

    #initializeUI(): void {
        this.#setupTextInputs();
        this.#setupFontControls();
        this.#setupFontSizeButtons();
        this.#setupStyleToggles();
        this.#setupColorControls();
        this.#setupUndoRedoSync();
        this.#setDefaultText();
        this.#updateStyleToggleUI();
        this.#updateTextScale();
        // The initial font face + shrink-to-fit run via `ready` (default family apply)
    }

    /** Syncs input fields with layer text after undo/redo actions */
    #setupUndoRedoSync(): void {
        const syncInputs = () => {
            const [textOne, textTwo, textThree] = this.#getTextLayers();
            if (this.#textOneInput && textOne) {
                this.#textOneInput.value = textOne.getText();
            }
            if (this.#textTwoInput && textTwo) {
                this.#textTwoInput.value = textTwo.getText();
            }
            if (this.#textThreeInput && textThree) {
                this.#textThreeInput.value = textThree.getText();
            }
        };

        this.#engine.on('actionUndone', syncInputs);
        this.#engine.on('actionRedone', syncInputs);
    }

    #setDefaultText(): void {
        const [textOne, textTwo, textThree] = this.#getTextLayers();
        textOne?.setText(DEFAULT_TEXT_ONE);
        textTwo?.setText(DEFAULT_TEXT_TWO);
        textThree?.setText(DEFAULT_TEXT_THREE);

        // Sync input fields
        if (this.#textOneInput) {
            this.#textOneInput.value = DEFAULT_TEXT_ONE;
        }
        if (this.#textTwoInput) {
            this.#textTwoInput.value = DEFAULT_TEXT_TWO;
        }
        if (this.#textThreeInput) {
            this.#textThreeInput.value = DEFAULT_TEXT_THREE;
        }
    }

    #setupTextInputs(): void {
        this.#textOneInput = document.querySelector<HTMLInputElement>('#text-one-input');
        this.#textTwoInput = document.querySelector<HTMLInputElement>('#text-two-input');
        this.#textThreeInput = document.querySelector<HTMLInputElement>('#text-three-input');

        if (!this.#textOneInput || !this.#textTwoInput || !this.#textThreeInput) {
            console.error('Text inputs not found');
            return;
        }

        // Text One input handler
        this.#textOneInput.addEventListener('input', () => {
            const text = this.#textOneInput?.value ?? '';
            const layer = this.#engine.getLayer('text-one') as RibbonTextLayer | undefined;
            if (layer) {
                layer.setText(text);
                this.#updateTextScale();
                this.#debouncedShrinkTextOne();
                // Text-one change may cause intersection with text-two
                this.#debouncedCheckTextTwoIntersection();
            }
        });

        // Text Two input handler
        this.#textTwoInput.addEventListener('input', () => {
            const text = this.#textTwoInput?.value ?? '';
            const layer = this.#engine.getLayer('text-two') as RibbonTextLayer | undefined;
            if (layer) {
                layer.setText(text);
                this.#updateTextScale();
                this.#debouncedShrinkTextTwo();
            }
        });

        // Text Three input handler
        this.#textThreeInput.addEventListener('input', () => {
            const text = this.#textThreeInput?.value ?? '';
            const layer = this.#engine.getLayer('text-three') as RibbonTextLayer | undefined;
            if (layer) {
                layer.setText(text);
                this.#updateTextScale();
                this.#debouncedShrinkTextThree();
            }
        });
    }

    #setupFontControls(): void {
        // Find the font dropdown container
        const fontSection = document.querySelector('.pin-text-question-container:has(.font-dropdown)');
        if (!fontSection) {
            console.error('Font section not found');
            return;
        }

        const $dropdown = fontSection.querySelector('.font-dropdown');
        if (!$dropdown) {
            console.error('Font dropdown not found');
            return;
        }

        // Font family dropdown
        this.#fontFamilyDropdown = $dropdown as HTMLElement;
        this.#fontFamilyBtn = this.#fontFamilyDropdown.querySelector('.dropdown-toggle') as HTMLButtonElement;
        const fontFamilyMenu = this.#fontFamilyDropdown.querySelector('.dropdown-menu') as HTMLElement;

        // Register each family's regular face so dropdown items render in their own font
        RegisterDropdownFaces();

        // Populate font family dropdown
        if (fontFamilyMenu) {
            fontFamilyMenu.innerHTML = '';
            for (const font of FONTS) {
                fontFamilyMenu.appendChild(this.#createFontMenuItem(font));
            }
        }

        // Set initial font family display
        if (this.#fontFamilyBtn) {
            this.#fontFamilyBtn.textContent = DEFAULT_FONT_FAMILY;
        }
    }

    #setupFontSizeButtons(): void {
        const configs = [
            { inputId: 'text-one-input', layerName: 'text-one', sizeKey: 'textOne' as const },
            { inputId: 'text-two-input', layerName: 'text-two', sizeKey: 'textTwo' as const },
            { inputId: 'text-three-input', layerName: 'text-three', sizeKey: 'textThree' as const },
        ];

        for (const { inputId, layerName, sizeKey } of configs) {
            const $downBtn = document.querySelector<HTMLButtonElement>(`#${inputId}-font-size-down`);
            const $upBtn = document.querySelector<HTMLButtonElement>(`#${inputId}-font-size-up`);

            if ($downBtn) {
                $downBtn.addEventListener('click', () => {
                    this.#adjustFontSize(layerName, sizeKey, -1);
                });
            }

            if ($upBtn) {
                $upBtn.addEventListener('click', () => {
                    this.#adjustFontSize(layerName, sizeKey, 1);
                });
            }
        }
    }

    #adjustFontSize(layerName: string, sizeKey: 'textOne' | 'textTwo' | 'textThree', delta: number): void {
        const layer = this.#engine.getLayer(layerName) as RibbonTextLayer | undefined;
        if (!layer) {
            return;
        }

        // Read current size from layer (not internal state) so presets work correctly
        const currentSize = layer.getFont().size ?? this.#fontSizes[sizeKey];
        const newSize = Math.max(
            TextCustomizerUI.#minFontSize,
            Math.min(TextCustomizerUI.#maxFontSize, currentSize + delta),
        );

        if (newSize === currentSize) {
            return;
        }

        this.#fontSizes[sizeKey] = newSize;
        void layer.setFont({ size: newSize });

        this.#updateTextScale();

        // Check if increased size overflows - run immediately (no debounce)
        if (delta > 0) {
            const maxDist = sizeKey === 'textOne' ? this.#getTextOneMaxDistance() : MAX_PATH_DISTANCE[sizeKey];
            void this.#shrinkToFitLayer(layerName, sizeKey, maxDist);
        }

        // Check text-two intersection when either text-one or text-two size changes
        if (sizeKey === 'textOne' || sizeKey === 'textTwo') {
            void this.#shrinkTextTwoToAvoidIntersection();
        }
    }

    #createFontMenuItem(font: FontConfig): HTMLLIElement {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'dropdown-item';
        a.href = '#';
        a.textContent = font.name;
        a.style.fontFamily = font.name;
        a.addEventListener('click', e => {
            e.preventDefault();
            void this.#applyFontFamily(font.name);
        });
        li.appendChild(a);
        return li;
    }

    /** Load the face for the current family+styles and apply it to all layers */
    async #applyCurrentFace(): Promise<void> {
        const seq = ++this.#faceSeq;
        let face: ResolvedFontFace;
        try {
            face = await ResolveFontFace(this.#currentFont.name, this.#fontStyles.bold, this.#fontStyles.italic);
        } catch (err) {
            // Keep whatever face is currently applied rather than falling back
            // to a device font the print file couldn't match
            console.error('failed to load font face', err);
            return;
        }
        // A newer family/style change superseded this one while the file loaded
        if (seq !== this.#faceSeq) {
            return;
        }

        this.#applyToAllLayers(layer => layer.setFont({
            family: face.family,
            weight: face.weight,
            style: face.style,
            textDecoration: this.#fontStyles.underline ? 'underline' : 'none',
            fontUrl: face.fontUrl,
            exactFontFace: true,
        }));

        this.#updateTextScale();

        // Face changes may overflow at the same size - check all layers
        await this.#shrinkAllLayersToFit();
    }

    async #applyFontFamily(fontFamily: string): Promise<void> {
        this.#currentFont = FontByName(ResolveFontFamily(fontFamily)) ?? FONTS[0];

        if (this.#fontFamilyBtn) {
            this.#fontFamilyBtn.textContent = this.#currentFont.name;
        }

        // Styles the new family has no real face for turn off instead of synthesizing
        const faces = this.#currentFont.faces;
        if (!faces.bold) {
            this.#fontStyles.bold = false;
        }
        if (!faces.italic) {
            this.#fontStyles.italic = false;
        }
        if (this.#boldBtn) {
            this.#boldBtn.disabled = !faces.bold;
        }
        if (this.#italicBtn) {
            this.#italicBtn.disabled = !faces.italic;
        }
        this.#updateStyleToggleUI();

        await this.#applyCurrentFace();
    }

    /** Run shrink-to-fit on all text layers */
    async #shrinkAllLayersToFit(): Promise<void> {
        await Promise.all([
            this.#shrinkToFitLayer('text-one', 'textOne', this.#getTextOneMaxDistance()),
            this.#shrinkToFitLayer('text-two', 'textTwo', MAX_PATH_DISTANCE.textTwo),
            this.#shrinkToFitLayer('text-three', 'textThree', MAX_PATH_DISTANCE.textThree),
        ]);
        // After shrinking to path bounds, check text-two intersection with text-one
        await this.#shrinkTextTwoToAvoidIntersection();
    }

    #measureTextMetrics(text: string, fontFamily: string, fontSize: number, fontWeight: string): {
        ascent: number;
        descent: number;
        height: number;
    } {
        if (!text) {
            return { ascent: 0, descent: 0, height: 0 };
        }

        this.#measureCtx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        const metrics = this.#measureCtx.measureText(text);

        return {
            ascent: metrics.actualBoundingBoxAscent,
            descent: metrics.actualBoundingBoxDescent,
            height: metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent,
        };
    }

    #calculateTextScaleRatio(text: string): number {
        const [topLayer] = this.#getTextLayers();
        if (!topLayer || !text) {
            return 1;
        }

        const font = topLayer.getFont();
        const fontFamily = font.family ?? 'Arial';
        const fontWeight = font.weight ?? 'bold';
        const measureSize = 100;

        const baselineHeight = this.#measureTextMetrics(BASELINE_TEXT, fontFamily, measureSize, fontWeight).height;
        if (baselineHeight === 0) {
            return 1;
        }

        const combinedHeight = this.#measureTextMetrics(BASELINE_TEXT + text, fontFamily, measureSize, fontWeight).height;

        if (combinedHeight > baselineHeight) {
            return baselineHeight / combinedHeight;
        }

        return 1;
    }

    #updateTextScale(): void {
        const [textOne, textTwo, textThree] = this.#getTextLayers();
        const textOneValue = this.#textOneInput?.value ?? '';
        const textTwoValue = this.#textTwoInput?.value ?? '';
        const textThreeValue = this.#textThreeInput?.value ?? '';

        if (textOne) {
            const textOneScale = this.#calculateTextScaleRatio(textOneValue);
            void textOne.setFont({ scale: textOneScale < 1 ? textOneScale : undefined });
        }

        if (textTwo) {
            const textTwoScale = this.#calculateTextScaleRatio(textTwoValue);
            void textTwo.setFont({ scale: textTwoScale < 1 ? textTwoScale : undefined });
        }

        if (textThree) {
            const textThreeScale = this.#calculateTextScaleRatio(textThreeValue);
            void textThree.setFont({ scale: textThreeScale < 1 ? textThreeScale : undefined });
        }
    }

    /**
     * Shrink a layer's font until text fits the path — checks both perpendicular overflow (too tall)
     * and axial overflow (too long for the path), applying whichever shrink happens first.
     */
    async #shrinkToFitLayer(
        layerName: string,
        sizeKey: 'textOne' | 'textTwo' | 'textThree',
        maxDistance: number,
    ): Promise<void> {
        const layer = this.#engine.getLayer(layerName) as RibbonTextLayer | undefined;
        if (!layer?.getText()) {
            return;
        }

        const applyShrink = (result: { fontSizePt: number; didShrink: boolean } | null): void => {
            if (!result?.didShrink) {
                return;
            }
            const newSize = Math.min(result.fontSizePt, TextCustomizerUI.#maxFontSize);
            this.#fontSizes[sizeKey] = newSize;
            void layer.setFont({ size: newSize });
            this.#updateTextScale();
        };

        applyShrink(await layer.shrinkToFitPathPtAsync(maxDistance, TextCustomizerUI.#minFontSize));
        applyShrink(await layer.shrinkToFitPathLengthPtAsync(TextCustomizerUI.#minFontSize));
    }

    /**
     * Shrink text-two to fit path bounds, then check for intersection with text-one.
     */
    async #shrinkTextTwoWithIntersectionCheck(): Promise<void> {
        // First shrink to fit path bounds
        await this.#shrinkToFitLayer('text-two', 'textTwo', MAX_PATH_DISTANCE.textTwo);
        // Then check for intersection with text-one
        await this.#shrinkTextTwoToAvoidIntersection();
    }

    /**
     * Shrink text-two if it intersects with text-one.
     * Uses pixel-level intersection detection.
     */
    async #shrinkTextTwoToAvoidIntersection(): Promise<void> {
        const textOne = this.#engine.getLayer('text-one') as RibbonTextLayer | undefined;
        const textTwo = this.#engine.getLayer('text-two') as RibbonTextLayer | undefined;

        if (!textOne?.getText() || !textTwo?.getText()) {
            return;
        }

        const result = await textTwo.shrinkToAvoidIntersectionPtAsync(textOne, TextCustomizerUI.#minFontSize);
        if (!result) {
            return;
        }

        if (result.didShrink) {
            const newSize = Math.min(result.fontSizePt, TextCustomizerUI.#maxFontSize);
            this.#fontSizes.textTwo = newSize;
            void textTwo.setFont({ size: newSize });
            this.#updateTextScale();
        }
    }

    #setupStyleToggles(): void {
        this.#boldBtn = document.querySelector<HTMLButtonElement>('#font-bold-btn');
        this.#italicBtn = document.querySelector<HTMLButtonElement>('#font-italic-btn');
        this.#underlineBtn = document.querySelector<HTMLButtonElement>('#font-underline-btn');

        if (this.#boldBtn) {
            this.#boldBtn.addEventListener('click', () => {
                this.#fontStyles.bold = !this.#fontStyles.bold;
                this.#updateStyleToggleUI();
                void this.#applyCurrentFace();
            });
        }

        if (this.#italicBtn) {
            this.#italicBtn.addEventListener('click', () => {
                this.#fontStyles.italic = !this.#fontStyles.italic;
                this.#updateStyleToggleUI();
                void this.#applyCurrentFace();
            });
        }

        if (this.#underlineBtn) {
            this.#underlineBtn.addEventListener('click', () => {
                this.#fontStyles.underline = !this.#fontStyles.underline;
                this.#updateStyleToggleUI();
                void this.#applyCurrentFace();
            });
        }
    }

    #updateStyleToggleUI(): void {
        if (this.#boldBtn) {
            this.#boldBtn.classList.toggle('active', this.#fontStyles.bold);
            this.#boldBtn.setAttribute('aria-pressed', String(this.#fontStyles.bold));
        }

        if (this.#italicBtn) {
            this.#italicBtn.classList.toggle('active', this.#fontStyles.italic);
            this.#italicBtn.setAttribute('aria-pressed', String(this.#fontStyles.italic));
        }

        if (this.#underlineBtn) {
            this.#underlineBtn.classList.toggle('active', this.#fontStyles.underline);
            this.#underlineBtn.setAttribute('aria-pressed', String(this.#fontStyles.underline));
        }
    }

    #setupColorControls(): void {
        // Set up per-line color pickers
        this.#textOneColorPicker = document.querySelector<HTMLInputElement>('#color-text-one-input');
        this.#textOneColorIndicator = document.querySelector<HTMLElement>('#color-indicator-text-one-input');
        this.#textTwoColorPicker = document.querySelector<HTMLInputElement>('#color-text-two-input');
        this.#textTwoColorIndicator = document.querySelector<HTMLElement>('#color-indicator-text-two-input');
        this.#textThreeColorPicker = document.querySelector<HTMLInputElement>('#color-text-three-input');
        this.#textThreeColorIndicator = document.querySelector<HTMLElement>('#color-indicator-text-three-input');

        // Line one color picker
        if (this.#textOneColorPicker) {
            this.#textOneColorPicker.addEventListener('input', () => {
                const color = this.#textOneColorPicker?.value ?? '#ffffff';
                const layer = this.#engine.getLayer('text-one') as RibbonTextLayer | undefined;
                if (layer) {
                    layer.setColor(color);
                }
                if (this.#textOneColorIndicator) {
                    this.#textOneColorIndicator.style.backgroundColor = color;
                }
            });
            // Initialize color indicator
            if (this.#textOneColorIndicator) {
                this.#textOneColorIndicator.style.backgroundColor = this.#textOneColorPicker.value;
            }
            // Apply initial color to layer
            const layer = this.#engine.getLayer('text-one') as RibbonTextLayer | undefined;
            if (layer) {
                layer.setColor(this.#textOneColorPicker.value);
            }
        }

        // Line two color picker
        if (this.#textTwoColorPicker) {
            this.#textTwoColorPicker.addEventListener('input', () => {
                const color = this.#textTwoColorPicker?.value ?? '#ffffff';
                const layer = this.#engine.getLayer('text-two') as RibbonTextLayer | undefined;
                if (layer) {
                    layer.setColor(color);
                }
                if (this.#textTwoColorIndicator) {
                    this.#textTwoColorIndicator.style.backgroundColor = color;
                }
            });
            // Initialize color indicator
            if (this.#textTwoColorIndicator) {
                this.#textTwoColorIndicator.style.backgroundColor = this.#textTwoColorPicker.value;
            }
            // Apply initial color to layer
            const layer = this.#engine.getLayer('text-two') as RibbonTextLayer | undefined;
            if (layer) {
                layer.setColor(this.#textTwoColorPicker.value);
            }
        }

        // Line three color picker
        if (this.#textThreeColorPicker) {
            this.#textThreeColorPicker.addEventListener('input', () => {
                const color = this.#textThreeColorPicker?.value ?? '#ffffff';
                const layer = this.#engine.getLayer('text-three') as RibbonTextLayer | undefined;
                if (layer) {
                    layer.setColor(color);
                }
                if (this.#textThreeColorIndicator) {
                    this.#textThreeColorIndicator.style.backgroundColor = color;
                }
            });
            // Initialize color indicator
            if (this.#textThreeColorIndicator) {
                this.#textThreeColorIndicator.style.backgroundColor = this.#textThreeColorPicker.value;
            }
            // Apply initial color to layer
            const layer = this.#engine.getLayer('text-three') as RibbonTextLayer | undefined;
            if (layer) {
                layer.setColor(this.#textThreeColorPicker.value);
            }
        }
    }

    /**
     * Notify that line 1's path has changed (switches between text-path-1 and text-path-4).
     * Call this when the path changes to update bounds.
     * @param usingAltPath - true if using text-path-4 (line 2 empty), false if using text-path-1
     */
    notifyTextOnePathChanged(usingAltPath: boolean): void {
        this.#textOneUsingAltPath = usingAltPath;

        // Re-check shrink-to-fit with new bounds
        const maxDist = usingAltPath ? MAX_PATH_DISTANCE.textOneAlt : MAX_PATH_DISTANCE.textOne;
        void this.#shrinkToFitLayer('text-one', 'textOne', maxDist);
    }

    /** Get the current max distance for text-one based on which path is active */
    #getTextOneMaxDistance(): number {
        return this.#textOneUsingAltPath ? MAX_PATH_DISTANCE.textOneAlt : MAX_PATH_DISTANCE.textOne;
    }
}
